import { useCallback, useEffect, useRef, useState } from 'react';
import { FeatureLayer, Graphic, WebMap } from '../constructors';
import { EntityIdToObjectIdMaps } from '../helpers/entity-id-to-object-id-maps';
import { createPolylineFeatureLayer } from '../helpers/feature-layers';
import { createPolylineGraphic } from '../helpers/graphics';
import { isDifferentPolyline } from '../helpers/polylines';
import type { MapViewportDerivedPolyline } from '../types';
import { useLayerControl } from './use-feature-layer-control';
import { useFeatureLayerCleanup } from './use-feature-layer-cleanup';

type PolylineFeatureLayerProps = {
    mapViewportPolylines?: MapViewportDerivedPolyline[];
    webMap?: WebMap;
};
export const usePolylineFeatureLayer = ({
    mapViewportPolylines = [],
    webMap,
}: PolylineFeatureLayerProps) => {
    const [polylineLayer, setPolylineLayer] = useState<FeatureLayer>();

    const entityIdToObjectIdMapCacheRef = useRef<typeof EntityIdToObjectIdMaps.POLYLINES>(
        EntityIdToObjectIdMaps.POLYLINES
    );

    const didPolylineGraphicChange = useCallback(
        (currPolyline: MapViewportDerivedPolyline, prevPolyline: MapViewportDerivedPolyline) => {
            return isDifferentPolyline(currPolyline, prevPolyline);
        },
        []
    );

    const createPolylineGraphics = useCallback(
        (mapViewportPolylines: MapViewportDerivedPolyline[]) => {
            const polylineGraphics: Graphic[] = [];
            for (const mapViewportPolyline of mapViewportPolylines) {
                const objectId = entityIdToObjectIdMapCacheRef.current.get(
                    mapViewportPolyline.entityId
                );
                const polylineGraphic = createPolylineGraphic({
                    objectId,
                    mapViewportPolyline,
                });
                if (polylineGraphic) {
                    polylineGraphics.push(polylineGraphic);
                }
            }
            return polylineGraphics;
        },
        []
    );

    useEffect(() => {
        if (webMap) {
            const polylineFeatureLayer = createPolylineFeatureLayer();
            setPolylineLayer(polylineFeatureLayer);
            webMap.layers.add(polylineFeatureLayer);
        }
    }, [webMap]);

    useFeatureLayerCleanup({
        layer: polylineLayer,
        webMap,
        cacheKey: 'POLYLINES',
        entityIdToObjectIdMapCacheRef,
        currentItems: mapViewportPolylines,
        cleanupStrategy: 'filter-stale',
        getCurrentEntityIds: (items) => items.map((item) => item.entityId),
    });

    useLayerControl<MapViewportDerivedPolyline>({
        layer: polylineLayer,
        currItems: mapViewportPolylines,
        didItemChange: didPolylineGraphicChange,
        createGraphics: createPolylineGraphics,
        entityIdToObjectIdMapCacheRef,
    });
};
